// The DM's fog of war controls over the board (issue 851). The canvas draws
// the covered squares and the box being dragged and feeds the fog tool; this
// is where the fog is switched on for the scene, reveal or cover is chosen,
// and the whole map is revealed or covered at once — the last asked first,
// because it takes away everything the players can see. Every change is one
// event through the table store; the server stamps the map's size on it.
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '../shell/Dialogs';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { fogTool, type FogDraft } from './fogTool';
import { pressWord } from '../shell/device';
import { BTN } from '../shell/ui';

const btn = BTN;

function useFogDraft(): FogDraft {
  const [d, setD] = useState(fogTool.get());
  useEffect(() => fogTool.subscribe(setD), []);
  return d;
}

/** A box released on the board becomes one event — the DM's own, never while looking through a player's eyes. */
export function FogGate() {
  const { act, role, viewingAs } = useTableState();
  const mayAct = useMayAct();
  // The fog is the DM's board's (`role`), and its tools the `map` power's (issue 1160).
  const dm = role === 'dm' && !viewingAs && mayAct('fog.reveal');
  // A player (or the DM looking through one's eyes) is never in the mode, however it was started.
  useEffect(() => { if (dm) return undefined; fogTool.stop(); return fogTool.subscribe((d) => { if (d.on) fogTool.stop(); }); }, [dm]);
  useEffect(() => (dm ? fogTool.onCommit((c) => act(c)) : undefined), [dm, act]);
  return null;
}

export function FogComposer() {
  const { act, role, viewingAs, store } = useTableState();
  const mayAct = useMayAct();
  const d = useFogDraft();
  const [asking, setAsking] = useState(false);
  const [askingOn, setAskingOn] = useState(false);
  // Escape leaves the tool from anywhere, not only with the board focused (issue 1028): after a click in this bar the
  // focus is here, and a DM who could not leave the mode went on painting fog where they meant to move a token.
  useEffect(() => {
    if (!d.on || asking || askingOn) return undefined;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') fogTool.stop(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [d.on, asking, askingOn]);
  if (!d.on || role !== 'dm' || viewingAs || !mayAct('fog.reveal')) return null;
  const on = store.state.fog?.on === true;
  const vision = store.state.vision?.on === true;
  // Switching the fog on with nothing revealed takes the whole map from the players at once, the same as Cover all,
  // so it asks first the same way (issue 1028).
  const revealedNothing = (store.state.fog?.revealed ?? []).length === 0;
  const toggle = () => { if (!on && revealedNothing) setAskingOn(true); else act({ kind: 'fog.set', on: !on }); };
  return (
    <div className="pointer-events-auto flex max-w-2xl flex-wrap items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="fog-composer" role="group" aria-label="Fog of war">
      <span className="font-medium text-amber-300">Fog of war</span>
      <label className="flex items-center gap-1">
        <input type="checkbox" checked={on} onChange={toggle} /> Fog on this scene
      </label>
      <span role="radiogroup" aria-label="What a box does" className="flex gap-0.5">
        {(['reveal', 'cover'] as const).map((m) => (
          <button key={m} type="button" role="radio" aria-checked={d.mode === m} className={`${btn} ${d.mode === m ? 'border-amber-500 text-amber-300' : ''}`} onClick={() => fogTool.mode(m)}>
            {m === 'reveal' ? 'Reveal' : 'Cover'}
          </button>
        ))}
      </span>
      <button type="button" className={btn} onClick={() => act({ kind: 'fog.revealAll' })}>Reveal all</button>
      <button type="button" className={btn} onClick={() => setAsking(true)}>Cover all…</button>
      <span className="text-stone-400" data-testid="fog-hint">
        {on ? `Drag a box of squares, or ${pressWord().toLowerCase()} one, to ${d.mode === 'reveal' ? 'reveal it' : 'cover it again'}. Shaded squares are what the players cannot see.` : 'The fog is off: the players see the whole map. Squares you reveal now stay revealed when it goes on.'}
        {/* With vision on, sight wins (#1409, Tom's C): a reveal shows the ground there dimmed, as ground a character has seen. */}
        {vision && ' With vision on, a revealed square shows the players the map there dimmed, like ground they have explored; what stands on it shows only once a character can see it.'}
      </span>
      <button type="button" className={btn} title="Leave the fog tool (Escape). Until then a drag on the board paints fog instead of moving a token." onClick={() => fogTool.stop()}>Done</button>
      {askingOn && (
        <ConfirmDialog title="Turn the fog on?" confirmLabel="Turn it on" cancelLabel="Leave it off" destructive
          body={<p>Nothing is revealed yet on this scene, so the whole map goes dark for the players at once. Their own characters stay visible to them. You can reveal squares afterwards, or turn it on and reveal first.</p>}
          onConfirm={() => { act({ kind: 'fog.set', on: true }); setAskingOn(false); }} onClose={() => setAskingOn(false)} />
      )}
      {asking && (
        <ConfirmDialog title="Cover the whole map?" confirmLabel="Cover all" cancelLabel="Cancel" destructive
          body={<p>Everything the players can see on this scene goes dark for them, and anything standing there leaves their sight. Their own characters stay visible to them.</p>}
          onConfirm={() => { act({ kind: 'fog.coverAll' }); setAsking(false); }} onClose={() => setAsking(false)} />
      )}
    </div>
  );
}
